/* eslint-disable import/no-cycle */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable complexity */
import {User} from '@hconnect/apiclient'
import {useNotification, Typography} from '@hconnect/uikit'
import {Button} from '@hm/fundamental'
import {Box, CircularProgress} from '@material-ui/core'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CloseIcon from '@mui/icons-material/Close'
import {AxiosError} from 'axios'
import moment from 'moment-timezone'
import {useState, useEffect} from 'react'
import {useTranslation} from 'react-i18next'
import {useSelector} from 'react-redux'

import {trackEvent} from '../../../../common/analytics'
import {useCancelOrder} from '../../../../common/react-query/hooks/mutations/useCancelOrder'
import {useGetOrderCancellationOptions} from '../../../../common/react-query/hooks/queries/useGetOrderCancellationOptions'
import {BusinessLineType} from '../../../../common/types'
import {CancellationRequestStatus} from '../../../../features/rmc/orderCancellation/types/cancellationRequest.types'
import {normalizeCancellationRequestStatus} from '../../../../features/rmc/orderCancellation/utils/normalizeCancellationRequestStatus'
import {OrderCancellationCutOffTime} from '../../../../OrderIntake/components/OrderCancellationCutOffTime'
import {OrderCancellation} from '../../../../OrderIntake/declarations/types'
import {RequestForQuoteResponse} from '../../../../OrderIntake/RequestForQuoteFormValues'
import {useOrderCancellationState} from '../../../../Organisms/Cancellation/OrderCancellationService'
import {useFeaturesState} from '../../../../Organisms/Features'
import {AppState} from '../../../../Root.store'
import {getUserProfile} from '../../../../UserProfile/UserProfile.selectors'
import {useCheckOrdersWithDeliveries} from '../../hooks/useCheckOrdersWithDeliveries'
import {Order} from '../../Order.types'

import {DisclaimerBanner} from './DisclaimerBanner'
import {getCemAggOrderCancellationInfo} from './getCemAggOrderCancellationInfo'
import {getRMCOrderCancellationInfo} from './getRMCOrderCancellationInfo'
import {useCancellationDisclaimerContents} from './hooks/useCancellationDisclaimers'

interface OrderCancellationButtonProps {
  order: Order
  disabled?: boolean
  onRequestSubmitted?: (request: CancellationRequestStatus) => void
  cancellationRequestStatus?: CancellationRequestStatus | null
  isErrorCancellationRequestStatus?: boolean
}

export const OrderCancellationButton: React.FC<OrderCancellationButtonProps> = ({
  order,
  disabled = false,
  onRequestSubmitted,
  cancellationRequestStatus,
  isErrorCancellationRequestStatus = false
}) => {
  const {t} = useTranslation()
  const {notify} = useNotification()
  const {mutate} = useCancelOrder()
  const userProfile = useSelector<AppState, User | null>(
    (state) => getUserProfile(state).userProfile
  )
  const {OrderCancellationState, setOrderCancellationState} = useOrderCancellationState()
  const [askForConfirmation, setAskForConfirmation] = useState(false)
  const [cancellationInfoText, setCancellationInfoText] = useState('')
  const [isLoadingCancellation, setIsLoadingCancellation] = useState(false)
  const [isButtonDisabled, setIsButtonDisabled] = useState(true)
  const [hideCancellationButton, setHideCancellationButton] = useState(false)
  const [isCancellationTimeOver, setIsCancellationTimeOver] = useState(false)

  const orderDeliveriesData = useCheckOrdersWithDeliveries(order)

  const {getFeature} = useFeaturesState()

  const {
    data: cancellationOptions,
    isLoading: isLoadingCancellationOptions,
    isError: isErrorCancellationOptions
  } = useGetOrderCancellationOptions(order.orderId, order.checkSum)

  const hideCutOffTime = isErrorCancellationOptions || hideCancellationButton

  const isCancelRequestFailedOnCheckSum: boolean = OrderCancellationState.some(
    (item) => item.orderItemNumber === order.orderNumber && item.isFailed
  )
  const isBusinessLineAllowed = cancellationOptions?.isBusinessLineAllowed

  const normalizedCancellationRequestStatus = normalizeCancellationRequestStatus(
    cancellationRequestStatus?.status
  )
  const isCancellationRequestSent = normalizedCancellationRequestStatus === 'sent'
  const isCancellationRequestCompleted = normalizedCancellationRequestStatus === 'completed'
  const isCancellationRequestFailed = normalizedCancellationRequestStatus === 'failed'
  const isCancellationRequestNotFound = cancellationRequestStatus === null

  const isOrderStatusCancelled = (order?.orderStatus as string) === 'cancelled'
  const isRmc = order.businessLine === BusinessLineType.RMC

  const {data: disclaimerData} = useCancellationDisclaimerContents(order.customer.countryId)

  const cancellationParams = {
    order,
    isCancellationRequestSent,
    isBusinessLineAllowed,
    isCancellationTimeOver,
    isCancellationRequestCompleted,
    isCancellationRequestNotFound,
    isOrderStatusCancelled,
    isErrorCancellationOptions,
    isErrorCancellationRequestStatus,
    isCancelRequestFailedOnCheckSum,
    cancellationInfoText,
    orderDeliveriesData,
    getFeature
  }

  const {getCancellationInfoText, isOrderCancellable} = isRmc
    ? getRMCOrderCancellationInfo(cancellationParams)
    : getCemAggOrderCancellationInfo(cancellationParams)

  const shouldButtonBeDisabled =
    !isOrderCancellable ||
    isLoadingCancellationOptions ||
    isErrorCancellationOptions ||
    !isBusinessLineAllowed ||
    isCancelRequestFailedOnCheckSum ||
    isCancellationRequestSent

  useEffect(() => {
    setIsButtonDisabled(shouldButtonBeDisabled)

    setIsLoadingCancellation(orderDeliveriesData.isDeliveriesFetching)
    setAskForConfirmation(false)
    setHideCancellationButton(false)

    if (isCancellationRequestSent) {
      setIsButtonDisabled(true)
    }

    if (isCancellationRequestCompleted) {
      setHideCancellationButton(true)
      setCancellationInfoText(t('orderIntake.orderCancelledInfo'))
    }

    if (isCancellationRequestFailed && !shouldButtonBeDisabled) {
      setIsButtonDisabled(false)
      setHideCancellationButton(false)
    }

    if (isCancellationRequestNotFound && isCancelRequestFailedOnCheckSum) {
      setIsButtonDisabled(true)
      setCancellationInfoText(t('orderIntake.cancellationFailedCheckSum'))
    }
  }, [
    shouldButtonBeDisabled,
    order,
    isErrorCancellationOptions,
    setIsButtonDisabled,
    isLoadingCancellationOptions,
    isOrderStatusCancelled,
    orderDeliveriesData,
    isCancellationRequestSent,
    isCancellationRequestCompleted,
    isCancellationRequestFailed
  ])

  const cancelOrder = () => {
    const orderId = order.orderId
    const cancelOrderPayload: OrderCancellation = {
      cancelReasonNotes: 'cancellation request',
      checkSum: order.checkSum,
      timeZone: moment.tz.guess()
    }

    setIsLoadingCancellation(true)
    setAskForConfirmation(false)
    trackEvent('hubOrderIntakeCancellationRequestSubmitted', {
      businessLine: order.businessLine,
      cancelledByOrderPlacer: order.orderPlacerName === userProfile?.name,
      country: order.customer.countryId,
      customerId: order.customer.customerId,
      orderId: order.orderId
    })

    mutate(
      {orderId, cancelOrderPayload},
      {
        onSuccess: (request) => {
          setIsLoadingCancellation(false)
          setHideCancellationButton(true)
          setCancellationInfoText(t('orderIntake.cancellationProcessing'))
          onRequestSubmitted?.(request)
        },
        onError: (error) => {
          setIsLoadingCancellation(false)

          const errorData = (error as AxiosError).response?.data as RequestForQuoteResponse
          const errorDetails = errorData?.detail

          if (!!errorDetails && errorDetails.includes('CheckSum is invalid')) {
            setIsButtonDisabled(true)
            setOrderCancellationState([
              ...OrderCancellationState,
              {isFailed: true, orderItemNumber: order.orderNumber}
            ])
            notify('error', t('orderIntake.cancellationFailedCheckSum'), {persist: true})
            setCancellationInfoText(t('orderIntake.cancellationFailedCheckSum'))

            trackEvent('hubOrderCancelOrderNotCancelled', {
              errorMessage: errorDetails,
              country: order.customer.countryId,
              customerId: order.customer.customerId,
              orderId: order.orderId
            })
          } else {
            notify('error', t('orderIntake.cancellationFailed'))
          }
        }
      }
    )
  }

  const handleAbortCancellation = () => {
    setAskForConfirmation(false)
  }

  const handleCancellation = () => {
    if (!askForConfirmation) {
      setAskForConfirmation(true)
      return
    } else {
      cancelOrder()
    }
  }

  const getConfirmationButtonContent = () => {
    if (isLoadingCancellation) {
      return <CircularProgress size={20} color="inherit" />
    } else if (isOrderStatusCancelled) {
      return t('orderIntake.orderCancelled')
    } else {
      return !askForConfirmation
        ? t('orderIntake.cancelOrder')
        : t('orderIntake.confirmCancellation')
    }
  }

  const handelTimeToCancelOver = (isTimeToCancelOver: boolean) => {
    /*
     If shouldButtonBeDisabled is true, we don't need to check for cut off time
    */
    if (shouldButtonBeDisabled || isButtonDisabled || isCancellationRequestSent) return

    setIsButtonDisabled(isTimeToCancelOver)
    setIsCancellationTimeOver(isTimeToCancelOver)
    isTimeToCancelOver && setCancellationInfoText(t('orderIntake.cancellationNotPossibleStatus'))
  }

  return (
    <>
      <Box
        width={1}
        mb={2}
        display="flex"
        flexDirection="column"
        alignItems="center"
        data-test-id="order-cancellation-buttons"
      >
        {!hideCancellationButton && !isOrderStatusCancelled ? (
          <Box width={1} display="flex" flexDirection="column" alignItems="center">
            {isRmc && askForConfirmation && !isButtonDisabled && (
              <Box width={1} mb={2}>
                <DisclaimerBanner
                  title={t('orderIntake.cancellationDisclaimerDefaultTitle')}
                  message={
                    disclaimerData?.data.content ?? t('orderIntake.cancellationDisclaimerDefault')
                  }
                  dataTestId="order-cancellation-disclaimer"
                />
              </Box>
            )}
            <Box width={1}>
              <Button
                variant="negative"
                size="large"
                onClick={handleCancellation}
                icon={isLoadingCancellation || isOrderStatusCancelled ? undefined : <CloseIcon />}
                disabled={isButtonDisabled || isLoadingCancellation || disabled}
                className="w-full"
                data-test-id="cancel-order-button"
              >
                {getConfirmationButtonContent()}
              </Button>
            </Box>
            <Box mt={2} />
            {askForConfirmation && (
              <Box width={1}>
                <Button
                  variant="secondary"
                  size="large"
                  onClick={handleAbortCancellation}
                  icon={<ArrowBackIcon />}
                  className="w-full"
                  data-test-id="abort-cancellation-button"
                >
                  {t('orderIntake.abortCancellation')}
                </Button>
              </Box>
            )}
          </Box>
        ) : null}
        {(isButtonDisabled || hideCancellationButton) && !isOrderStatusCancelled ? (
          <Box textAlign={'center'}>
            <Typography
              variant="body2"
              customColor="textPrimarySoft"
              style={{fontWeight: 700, fontSize: 14}}
              data-test-id="cancellation-info-text"
            >
              {getCancellationInfoText()}
            </Typography>
          </Box>
        ) : null}
        <Box mt={1} />
        {!hideCutOffTime && (
          <OrderCancellationCutOffTime
            order={order}
            onCancelCutOffTimeReached={handelTimeToCancelOver}
            cancellationOptions={cancellationOptions}
            isLoadingCancellationOptions={isLoadingCancellationOptions}
            isCancellationButtonDisabled={shouldButtonBeDisabled || isButtonDisabled || disabled}
          />
        )}
      </Box>
    </>
  )
}
